import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  Box,
  IconButton,
  Drawer,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useSession } from '../SessionContext';
import { EXPANDED_SIDEBAR_WIDTH_PX } from '../settings/settingsHomeNavItems';

type DrawerContextType = {
  mobileOpen: boolean;
  isClosing: boolean;
  drawerCollapsed: boolean;
  handleDrawerToggle: () => void;
  handleDrawerClose: () => void;
  handleDrawerTransitionEnd: () => void;
};

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

const useDrawerContext = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawerContext must be used within a ResponsiveDrawer');
  }
  return context;
};

type DrawerProps = {
  children: React.ReactNode;
  id?: string;
  testId?: string;
};

type TogglerProps = {
  onClick?: () => void;
};

const Toggler = ({ onClick }: TogglerProps) => {
  const { handleDrawerToggle } = useDrawerContext();
  const { setDrawerCollapsed, drawerCollapsed } = useSession();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    if (drawerCollapsed) {
      setDrawerCollapsed(false);
    }
    handleDrawerToggle();
  };

  return (
    <IconButton
      aria-label="Menu toggle"
      color="inherit"
      onClick={handleClick}
      sx={{ display: { xs: 'inherit', md: 'none' } }}
    >
      <MenuIcon fontSize="inherit" />
    </IconButton>
  );
};

const Content = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const { mobileOpen, drawerCollapsed, handleDrawerClose, handleDrawerTransitionEnd } = useDrawerContext();

  const paperProps = {
    sx: {
      width: drawerCollapsed ? '44px' : `${EXPANDED_SIDEBAR_WIDTH_PX}px`,
      backgroundColor: theme.palette.secondary.main,
      backgroundImage: 'none',
      color: theme.palette.secondary.contrastText,
      transition: 'width 0.3s ease',
      overflowX: 'hidden',
      borderRight: `1px solid ${theme.palette.divider}`,
      borderRadius: 0,
    },
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onTransitionEnd={handleDrawerTransitionEnd}
        onClose={handleDrawerClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            backgroundColor: theme.palette.secondary.main,
            backgroundImage: 'none',
            color: theme.palette.secondary.contrastText,
          },
        }}
        slotProps={{
          root: {
            keepMounted: true,
          },
        }}
        PaperProps={{ ...paperProps }}
      >
        {children}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            backgroundColor: theme.palette.secondary.main,
            backgroundImage: 'none',
            color: theme.palette.secondary.contrastText,
          },
        }}
        PaperProps={paperProps}
      >
        {children}
      </Drawer>
    </>
  );
};

const ResponsiveDrawer = ({ children, id, testId }: DrawerProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { drawerCollapsed } = useSession();

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  return (
    <DrawerContext.Provider
      value={{
        mobileOpen,
        isClosing,
        drawerCollapsed,
        handleDrawerToggle,
        handleDrawerClose,
        handleDrawerTransitionEnd,
      }}
    >
      <Box
        id={id}
        data-testid={testId}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {children}
      </Box>
    </DrawerContext.Provider>
  );
};

ResponsiveDrawer.Toggler = Toggler;
ResponsiveDrawer.Content = Content;

export default ResponsiveDrawer;
